"use client";

import useTranslate from "@app/_hooks/useTranslate";
import { isWalleyCheckoutEnabled } from "@/config/checkout";
import { envConfig } from "@/config/env";
import CartWalleyWidget from "@app/_components/Checkout/components/Walley/CartWalleyWidget";
import StockWatchStatus from "@app/_components/CheckoutItem/StockWatchStatus";
import WalleyIcon from "@app/_icons/WalleyIcon/WalleyIcon";
import CartItem from "@components/CartItem";
import ProductSum from "@components/Checkout/ProductSum";
import Link from "@components/Link";
import { Button, Cart } from "@components/ngg";
import { useMarket } from "@context/marketContext";
import { AmericanExpressIcon, MastercardIcon, VisaIcon } from "@ngg/icons";
import type {
  CartMetadataInput,
  GetCheckoutDeliveryQuery,
  Cart as ICart,
  CartItem as Item,
} from "@server/gql/graphql";
import { useCartQuery } from "@server/hooks";
import type { IncomingItem } from "@server/utils/cart";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface CartActionProps {
  addCartItem: <T extends boolean = false>(
    items: IncomingItem[],
    async?: T,
  ) => T extends true ? Promise<any> : void;
  addPromotion: <T extends boolean = false>(
    discount: string,
    async?: T,
  ) => T extends true ? Promise<any> : void;
  removePromotion: <T extends boolean = false>(
    async?: T,
  ) => T extends true ? Promise<any> : void;
  clearCart: <T extends boolean = false>(
    async?: T,
  ) => T extends true ? Promise<any> : void;
  getDeliveryCheckout: (
    zipCode: string,
  ) => Promise<GetCheckoutDeliveryQuery["deliveries"] | undefined>;
  removeCartItem: <T extends boolean = false>(
    item: Item,
    async?: T,
  ) => T extends true ? Promise<any> : void;
  hideCart: () => void;
  showCart: () => void;
  updateCartItem: <T extends boolean = false>(
    item: Item,
    async?: T,
  ) => T extends true ? Promise<any> : void;
  updateCartMetadata: <T extends boolean = false>(
    metadata: CartMetadataInput,
    async?: T,
  ) => T extends true ? Promise<any> : void;
}

interface CartStateProps {
  cart?: ICart | null;
  isEmpty: boolean;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  cartTotal: number;
  error: {
    getCart?: boolean;
    addCartItem?: boolean;
    updateCartItem?: boolean;
    removeCartItem?: boolean;
    addPromotion?: boolean;
    removePromotion?: boolean;
    updateCartMetadata?: boolean;
    clearCart?: boolean;
    updateDeliveryOption?: boolean;
    addGiftcard?: boolean;
    removeGiftcard?: boolean;
  };
  loading: {
    getCart: boolean;
    addCartItem: boolean;
    updateCartItem: boolean;
    removeCartItem: boolean;
    addPromotion: boolean;
    removePromotion: boolean;
    updateCartMetadata: boolean;
    clearCart: boolean;
    updateDeliveryOption: boolean;
    addGiftcard: boolean;
    removeGiftcard: boolean;
  };
  success: {
    getCart: boolean;
    addCartItem: boolean;
    updateCartItem: boolean;
    removeCartItem: boolean;
    addPromotion: boolean;
    removePromotion: boolean;
    updateCartMetadata: boolean;
    clearCart: boolean;
    updateDeliveryOption: boolean;
    addGiftcard: boolean;
    removeGiftcard: boolean;
  };
}

export const CartStateContext = createContext<CartStateProps | undefined>(
  undefined,
);
export const CartActionContext = createContext<CartActionProps | undefined>(
  undefined,
);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const t = useTranslate();
  const marketConfig = useMarket().state.market;
  const pathname = usePathname();
  const [open, setOpen] = useState<boolean>(false);

  const {
    cart,
    error,
    loading,
    success,
    isEmpty,
    isError,
    isLoading,
    isSuccess,
    addCartItem,
    addPromotion,
    clearCart,
    getDeliveryCheckout,
    removeCartItem,
    removePromotion,
    updateCartItem,
    updateCartMetadata,
    updateDeliveryOption,
  } = useCartQuery();

  const showCart = () => setOpen(true);
  const hideCart = () => setOpen(false);

  const actions = useMemo(
    () => ({
      showCart,
      hideCart,
      addCartItem,
      addPromotion,
      clearCart,
      getDeliveryCheckout,
      removeCartItem,
      removePromotion,
      updateCartItem,
      updateCartMetadata,
      updateDeliveryOption,
    }),
    [
      addCartItem,
      addPromotion,
      clearCart,
      getDeliveryCheckout,
      removeCartItem,
      removePromotion,
      updateCartItem,
      updateCartMetadata,
      updateDeliveryOption,
    ],
  );

  const cartTotal = useMemo(
    () => (cart?.totalGrossAmount ?? 0) - (cart?.totalFreightInclVat ?? 0),
    [cart?.totalGrossAmount, cart?.totalFreightInclVat],
  );

  const state = useMemo(
    () => ({
      cart,
      error,
      loading,
      success,
      isEmpty,
      isError,
      isLoading,
      isSuccess,
      cartTotal,
    }),
    [
      cart,
      error,
      loading,
      success,
      isEmpty,
      isError,
      isLoading,
      isSuccess,
      cartTotal,
    ],
  );

  useEffect(() => setOpen(false), [pathname]);

  return (
    <CartStateContext.Provider value={state}>
      <CartActionContext.Provider value={actions}>
        <Cart
          open={open}
          isEmpty={(cart?.items ?? []).length === 0}
          EmptyCart={
            <div className="flex flex-col items-start justify-center gap-8 px-3 py-4">
              <p className="">{t("emptyCart")}</p>
              <button
                onClick={hideCart}
                type="button"
                className="all-small-caps w-full bg-black px-12 py-3 text-base uppercase tracking-wider text-white"
              >
                {t("startShopping")}
              </button>
            </div>
          }
          CartFooter={
            <div className="justify-self-end border-t border-grey-150 p-3 pb-7">
              <ProductSum
                showTotal={false}
                showShipping={false}
                cart={cart ?? undefined}
                className="mb-2"
                showVAT={false}
              />
              {isWalleyCheckoutEnabled({ marketConfig }) && (
                <CartWalleyWidget cartTotal={cartTotal} />
              )}
              <Button
                data-testid="Cart-button-goToCheckout"
                as={Link}
                locale={marketConfig.locale}
                className="btn-primary flex min-h-12 w-full items-center justify-center p-2"
                href="/checkout"
              >
                <p>{t("goToCheckout")}</p>
              </Button>
              <div className="m-auto mt-4 flex items-center justify-center gap-4">
                {isWalleyCheckoutEnabled({ marketConfig }) && <WalleyIcon />}
                <MastercardIcon />
                <VisaIcon />
                <AmericanExpressIcon />
              </div>
            </div>
          }
          hideCart={hideCart}
        >
          {cart?.items
            ?.filter(
              ({ partNo }) =>
                partNo !==
                `${envConfig.NEXT_PUBLIC_NSHIFT_DYNAMIC_FREIGHT_PARTNO}`,
            )
            ?.map((item) => (
              <CartItem.Root
                key={item.id}
                item={item}
                locale={marketConfig.locale}
                removeItem={removeCartItem}
                updateItem={updateCartItem}
                loading={loading.updateCartItem}
              >
                <CartItem.Remove />
                <CartItem.Image />
                <CartItem.Details>
                  <CartItem.Name className="col-span-2" />
                  <CartItem.Items />
                  <div className="flex flex-col gap-y-1.5">
                    <CartItem.Stock />
                    <CartItem.Quantity className="col-start-1 mt-auto" />
                  </div>
                  <CartItem.Price className="col-start-2 mt-auto text-right" />
                  <StockWatchStatus
                    stock={item.stockWatchStatus}
                    onRemove={() => removeCartItem(item)}
                    onUpdate={(quantity: number) =>
                      updateCartItem({ ...item, quantity })
                    }
                    className="col-span-full"
                  />
                </CartItem.Details>
              </CartItem.Root>
            ))
            .reverse()}
        </Cart>
        {children}
      </CartActionContext.Provider>
    </CartStateContext.Provider>
  );
};

export const useCartState = () => {
  const context = useContext(CartStateContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};

export const useCartActions = () => {
  const context = useContext(CartActionContext);

  if (!context) {
    throw new Error("useCartActions must be used within a CartProvider");
  }

  return context;
};
